import classNames from 'classnames';
import { Icon, IconsEnum } from 'components/Icons';
import useWindowSize from 'hooks/useWindowSize';
import { RootState, useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { createRef, useCallback } from 'react';
import { NumericFormat } from 'react-number-format';
import { isSnaiSite } from 'utility/constant';
import { isTruthy } from 'utility/functions';
import { selectMinValueByTipo, selectTipo, selectVariationByTipo } from '../carrelloSelectors';
import { customMobileInputToggled } from '../carrelloSlice';
import { TicketErrore, TicketErrorWithValue } from '../types';
import styles from './CarrelloInputImporto.module.scss';

export type CarrelloInputImportoProps = {
  hasBtn?: boolean;
  puntata: number;
  hasCurrency?: boolean;
  erroriCarrello?: TicketErrorWithValue<TicketErrore>[];
  externalHandler?: (_value: number) => void;
  onClick?: (_e: React.MouseEvent<HTMLInputElement>) => void;
  setPuntataInputValue: React.Dispatch<React.SetStateAction<number | undefined>>;
  isDisabled?: boolean;
  handleChangeQuotaInput?: (_e: React.MouseEvent<HTMLElement>) => void;
};

export const CarrelloInputImporto = ({
  hasBtn,
  puntata,
  onClick,
  hasCurrency = true,
  erroriCarrello,
  externalHandler,
  setPuntataInputValue,
  isDisabled,
  handleChangeQuotaInput,
}: CarrelloInputImportoProps) => {
  const dispatch = useAppDispatch();
  const tipo = useTypedSelector(selectTipo);
  const minInput = useTypedSelector(selectMinValueByTipo);
  const { isMobile } = useWindowSize();

  const stepVariation = useTypedSelector((state: RootState) => selectVariationByTipo(state, tipo));
  const numericRef = createRef<HTMLInputElement>();
  const isDecrementDisabled = puntata <= minInput;

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (isMobile && isSnaiSite) {
      e.preventDefault();
      dispatch(customMobileInputToggled(true));
    }
    if (typeof onClick === 'function') {
      onClick(e);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    if (isMobile && isSnaiSite) {
      dispatch(customMobileInputToggled(true));
      e.target.blur();
    }
  };

  const parseValue = (value: string) => {
    return parseFloat(value.replace(',', '.').replace('€', ''));
  };

  const updatePuntata = useCallback(
    (amount: number) => {
      if (numericRef.current) {
        const currentValue = numericRef.current.value === '' ? 0 : parseValue(numericRef.current.value);
        let value = currentValue + amount;
        if (value < 0) return;
        setPuntataInputValue(isNaN(value) ? undefined : value);
      }
    },
    [numericRef, setPuntataInputValue]
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event?.target ?? {};
    const nextValue = parseValue(value);
    setPuntataInputValue(isNaN(nextValue) ? undefined : nextValue);

    if (typeof externalHandler === 'function') {
      externalHandler(isNaN(nextValue) ? 0 : nextValue);
    }
  };

  const handleChangeQuotaInputButton = (e) => {
    if (isDisabled) {
      if (typeof handleChangeQuotaInput === 'function') {
        handleChangeQuotaInput(e);
      }
      return;
    }
    updatePuntata(stepVariation);
  };

  return (
    <div
      className={classNames(styles.inputContainer, {
        [styles.withBtn]: hasBtn,
      })}
    >
      {isSnaiSite ? (
        <>
          <button
            className={isDisabled ? styles.disabled : ''}
            onClick={() => updatePuntata(-stepVariation)}
            disabled={isDecrementDisabled || isDisabled}
          >
            <Icon
              iconId={IconsEnum.MINUS}
              className={classNames(styles.icon, { [styles.disabled]: isDecrementDisabled })}
            />
          </button>
          <div className={styles.currency}>
            <NumericFormat
              suffix={hasCurrency ? '€' : undefined}
              getInputRef={numericRef}
              min={minInput}
              max={'999999'}
              step={stepVariation}
              className={classNames(styles.input, {
                [styles.error]: isTruthy(erroriCarrello?.length),
                [styles.disabled]: isDisabled,
              })}
              value={!isDisabled ? puntata : ''}
              decimalScale={2}
              decimalSeparator={','}
              inputMode="none"
              allowLeadingZeros={true}
              allowNegative={false}
              thousandsGroupStyle={'thousand'}
              onFocus={handleFocus}
              onClick={handleClick}
              onChange={handleInputChange}
            />
          </div>
          <button className={isDisabled ? styles.disabled : ''} onClick={handleChangeQuotaInputButton}>
            <Icon iconId={IconsEnum.PLUS} className={styles.icon} color="var(--color-on-bg-secondary)" />
          </button>
        </>
      ) : (
        <input
          min={minInput}
          max="99999"
          type="number"
          step={stepVariation}
          value={puntata}
          className={styles.input}
          onClick={handleClick}
          onChange={handleInputChange}
        />
      )}
    </div>
  );
};
